<template>
  <div class="top-items-sales-widget-container top-items-sales-widget-container--glass">
    <div v-if="loading" class="dashboard-widget-glass-state">
      <i class="bi bi-arrow-repeat dashboard-widget-glass-spinner" aria-hidden="true"></i>
      <span>Loading sales data…</span>
    </div>
    <div v-else-if="error" class="dashboard-widget-glass-state dashboard-widget-glass-state--error">
      <i class="bi bi-exclamation-circle" aria-hidden="true"></i>
      <span>{{ error }}</span>
    </div>
    <div v-else class="dashboard-widget-glass-table-section consultant-glass-table-section">
      <div class="consultant-glass-table-responsive consultant-glass-table-responsive--framed">
        <div class="consultant-glass-table-scroll-x">
          <table class="consultant-glass-table top-items-sales-widget-table">
            <thead>
              <tr>
                <th>Item</th>
                <th class="dashboard-widget-glass-amount-header">Total Sales</th>
              </tr>
            </thead>
            <tbody>
              <template v-if="items.length === 0">
                <tr>
                  <td colspan="2" class="consultant-glass-table-no-records">No sales data</td>
                </tr>
              </template>
              <template v-else>
                <tr
                  v-for="(item, index) in items"
                  :key="item.item"
                  class="consultant-glass-table-row"
                  :style="{ animationDelay: `${0.04 + index * 0.03}s` }"
                >
                  <td>{{ item.item }}</td>
                  <td
                    class="dashboard-widget-glass-amount-cell"
                    :class="{ 'dashboard-widget-glass-amount-cell--empty': !item.amount }"
                  >{{ formatCurrency(item.amount) }}</td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import './styles/TopItemsBySalesWidget.css';
import SalesReportService from '@/services/salesReport.service';
import logger from '@/utils/logger';
import ApiService from '@/services/api.service';

export default {
  name: 'TopItemsBySalesWidget',
  props: {
    companyId: { type: [String, Number], required: true },
  },
  data() {
    return {
      items: [],
      loading: true,
      error: null,
    };
  },
  async mounted() {
    await this.fetchData();
  },
  methods: {
    async fetchData() {
      this.loading = true;
      this.error = null;
      try {
        const result = await SalesReportService.getSalesByItemSummary(this.companyId, {
          reportBasis: 'accrual',
        });

        if (result.success && result.data && result.data.length > 0) {
          this.items = result.data
            .sort((a, b) => b.amount - a.amount)
            .slice(0, 10);
        } else {
          await this.fetchItemsFromInvoices();
        }
      } catch (error) {
        logger.error('Error fetching top items by sales:', error);
        await this.fetchItemsFromInvoices();
      } finally {
        this.loading = false;
      }
    },
    async fetchItemsFromInvoices() {
      try {
        const response = await ApiService.getInvoiceItemsByCompany(this.companyId);
        const invoiceItems = Array.isArray(response) ? response : (response?.data || []);

        const itemSalesMap = new Map();

        invoiceItems.forEach((item) => {
          const itemName = item.description
            || item.itemName
            || item.consultant?.consultantName
            || 'Unnamed Item';
          const totalAmount = parseFloat(item.totalAmount || item.amount || item.total || 0);

          if (itemSalesMap.has(itemName)) {
            const existing = itemSalesMap.get(itemName);
            existing.amount += totalAmount;
          } else {
            itemSalesMap.set(itemName, {
              item: itemName,
              amount: totalAmount,
            });
          }
        });

        this.items = Array.from(itemSalesMap.values())
          .sort((a, b) => b.amount - a.amount)
          .slice(0, 10);
      } catch (error) {
        logger.error('Error fetching invoice items:', error);
        this.error = 'Failed to load data';
        this.items = [];
      }
    },
    formatCurrency(amount) {
      if (amount === null || amount === undefined || isNaN(amount)) return '$0.00';
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount);
    },
  },
};
</script>
